import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Modal } from 'reactstrap';
import { toast } from 'react-toastify';
import CloseIcon from 'mdi-react/CloseIcon';
import SquareIconButton from '../../../shared/components/custom/SquareIconButton';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import Api from '../../../util/api';
import useConfig from '../../../util/useConfig';
import { partial } from '../../../util/translation/translation';

// Every upcoming match for the club's teams, fetched once per open
const FETCH_SIZE = 500;
// Same limit as the questionnaire form's image
const MAX_IMAGE_MB = 3;

export const teamName = (match, side) =>
  (match.isCustom ? match.extraData?.[side] : match[side]?.name) || '-';

const teamLogo = (match, side) =>
  match.isCustom ? match.extraData?.[`${side}Logo`] : match[side]?.logoUrl;

const leagueName = (match) =>
  match.league?.name || match.extraData?.matchType || '';

let afterKey = 0;
const emptyAfterQuestion = (preset = {}) => {
  afterKey += 1;
  return {
    key: afterKey,
    text: '',
    type: 'options',
    options: ['', ''],
    ...preset,
  };
};

// The question created for a match: "Home – Away", guessed as a score
export const matchQuestionTitle = (match) =>
  `${teamName(match, 'homeTeam')} – ${teamName(match, 'awayTeam')}`;

/**
 * Pick upcoming matches; each becomes a score predictor question with the
 * two teams as its rows. The correct score is left empty until the match
 * has been played, so no guess earns points before then.
 */
// mode "questions": add the questions to one questionnaire (onCreate gets
// the questions). mode "quizzes": one quiz per match (onCreate also gets the
// matches and the quiz settings: category and answer visibility).
const MatchQuestionsModal = ({
  isOpen,
  onClose,
  onCreate,
  existingTitles,
  mode,
}) => {
  const isQuizzes = mode === 'quizzes';
  const isPolls = mode === 'polls';
  // Quizzes and polls: pick matches, then settings in a second step
  const isSteps = isQuizzes || isPolls;
  const config = useConfig();
  const p = partial('questionaire');
  const qT = partial('question');
  const s = partial('shared');
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [league, setLeague] = useState('');
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  // Quizzes mode is two steps: pick matches, then the quiz settings
  const [step, setStep] = useState(1);
  // Quizzes and polls get a third step: push notifications
  const lastStep = isSteps ? 3 : 2;
  // Quizzes: a reminder push that opens the quiz, hours before kick-off
  const [pushQuiz, setPushQuiz] = useState({});
  const [pushBefore, setPushBefore] = useState({});
  const [pushAfter, setPushAfter] = useState({});
  // Points for guessing the exact result, the same for every created question
  const [points, setPoints] = useState('1');
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  // Quizzes mode: one image for every quiz, and library questions added
  // after the match question in each
  const [image, setImage] = useState(null);
  const [subtitle, setSubtitle] = useState('');
  // Polls mode: a poll before and/or after each match
  const [beforeOn, setBeforeOn] = useState(true);
  const [beforeQuestion, setBeforeQuestion] = useState('');
  const [includeDraw, setIncludeDraw] = useState(true);
  // Hours before kick-off the poll opens (6 by default); empty opens it
  // right away
  const [beforeHours, setBeforeHours] = useState('6');
  // After the match: one or more questions, each with its own answer
  // options or a player from the squad
  const [afterOn, setAfterOn] = useState(false);
  const [afterQuestions, setAfterQuestions] = useState([emptyAfterQuestion()]);
  const [afterStartHours, setAfterStartHours] = useState('2');
  const [afterOpenHours, setAfterOpenHours] = useState('48');
  const [library, setLibrary] = useState([]);
  const [extraIds, setExtraIds] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    setSelected([]);
    setLeague('');
    setSearch('');
    setStep(1);
    setPushBefore({
      on: true,
      title: p('pushBeforeTitle'),
      message: p('pushBeforeMessage'),
    });
    setPushAfter({
      on: true,
      title: p('pushAfterTitle'),
      message: p('pushAfterMessage'),
    });
    setPushQuiz({
      on: true,
      title: p('pushQuizTitle'),
      message: p('pushQuizMessage'),
      hours: '6',
    });
    setPoints('1');
    setCategoryId('');
    setImage(null);
    setSubtitle(p('guessTheResult'));
    setBeforeOn(true);
    setBeforeQuestion(p('pollWhoWins'));
    setIncludeDraw(true);
    setBeforeHours('6');

    setAfterOn(false);
    setAfterQuestions([emptyAfterQuestion()]);
    setAfterStartHours('2');
    setAfterOpenHours('48');
    setExtraIds([]);
    if (isSteps) {
      Api.questionaire.categories
        .getQuestionaireCategories()
        .then((data) => setCategories(Array.isArray(data) ? data : []))
        .catch(() => setCategories([]));
      Api.questionaire
        .getQuestions(
          { page: 1, take: 1000 },
          { orderBy: 'createdAt', direction: 'DESC' }
        )
        .then((res) => setLibrary(res?.data || []))
        .catch(() => setLibrary([]));
    }
    setLoading(true);
    Api.matchCenter
      .getUpcomingMatches(
        1,
        FETCH_SIZE,
        (config.teams || []).map((team) => team.id),
        false
      )
      .then((res) =>
        setMatches(
          (res?.data?.matches || []).sort(
            (a, b) => new Date(a.matchStart) - new Date(b.matchStart)
          )
        )
      )
      .catch(() => toast.error(s('somethingWentWrong')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [isOpen]);

  const leagues = useMemo(
    () => [...new Set(matches.map(leagueName).filter(Boolean))],
    [matches]
  );
  const query = search.trim().toLowerCase();
  const visible = matches.filter(
    (m) =>
      (!league || leagueName(m) === league) &&
      (!query || matchQuestionTitle(m).toLowerCase().includes(query))
  );
  const chosenMatches = matches.filter((m) => selected.includes(m.id));
  const isAdded = (m) => existingTitles.includes(matchQuestionTitle(m));
  const selectable = visible.filter((m) => !isAdded(m));
  const allSelected =
    selectable.length > 0 && selectable.every((m) => selected.includes(m.id));

  const toggle = (id) =>
    setSelected((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]
    );

  const toggleAll = () =>
    setSelected((ids) =>
      allSelected
        ? ids.filter((id) => !selectable.some((m) => m.id === id))
        : [...new Set([...ids, ...selectable.map((m) => m.id)])]
    );

  const pointValue = Math.max(0, parseInt(points, 10) || 0);

  const pickImage = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      toast.error(p('imageTooLarge', { size: MAX_IMAGE_MB }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result);
    reader.readAsDataURL(file);
  };

  const toggleExtra = (id) =>
    setExtraIds((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]
    );

  const create = () => {
    const chosen = matches.filter((m) => selected.includes(m.id));
    onCreate(
      chosen.map((match) => ({
        matchId: match.id,
        title: matchQuestionTitle(match),
        description: [
          moment.utc(match.matchStart).local().format('dddd D MMMM HH:mm'),
          leagueName(match),
        ]
          .filter(Boolean)
          .join(' · '),
        type: 'SCORE_PREDICTOR',
        pointValue: pointValue,
        imageHeaderType: 'NONE',
        headingVideos: [],
        isRequired: false,
        value: {
          pointValue: pointValue,
          availableOptions: [
            { id: 0, title: teamName(match, 'homeTeam'), isValidAnswer: null },
            { id: 1, title: teamName(match, 'awayTeam'), isValidAnswer: null },
          ],
        },
      })),
      {
        matches: chosen,
        categoryId: categoryId || null,
        image,
        // Per match: the reminder push that opens its quiz (or none)
        pushes: chosen.map((match) => {
          const push = pushFor(pushQuiz, match);
          if (!push) return null;
          const kickOff = moment.utc(match.matchStart);
          return {
            ...push,
            sendAt: moment
              .max(
                moment(),
                pushQuiz.hours === ''
                  ? moment()
                  : kickOff
                      .clone()
                      .subtract(Number(pushQuiz.hours) || 0, 'hours')
              )
              .toISOString(),
          };
        }),
        // Empty falls back to the default, since a quiz needs a subtitle
        subtitle: subtitle.trim() || p('guessTheResult'),
        // In the order they were ticked
        extraQuestions: extraIds
          .map((id) => library.find((q) => q.id === id))
          .filter(Boolean),
      }
    );
  };

  // Polls: every poll as a questionnaire plus its one question
  const filledOptions = (question) =>
    question.options.map((o) => o.trim()).filter(Boolean);
  const afterValid =
    !afterOn ||
    afterQuestions.every(
      (question) =>
        !!question.text.trim() &&
        (question.type === 'player' || filledOptions(question).length >= 2)
    );
  const pollsValid = (beforeOn || afterOn) && afterValid;
  const pollCount =
    chosenMatches.length * ((beforeOn ? 1 : 0) + (afterOn ? 1 : 0));

  const radioQuestion = (title, options) => ({
    title,
    description: '',
    type: 'RADIO_BUTTONS',
    isRequired: true,
    headingVideos: [],
    imageHeaderType: 'NONE',
    value: {
      availableOptions: options.map((option, id) => ({
        id,
        title: option,
        isValidAnswer: false,
      })),
    },
  });

  // Ready-made after-match questions
  const afterPresets = [
    {
      id: 'nextHome',
      label: p('presetNextHome'),
      question: {
        text: p('presetNextHomeQuestion'),
        options: [p('presetYes'), p('presetMaybe'), p('presetNo')],
      },
    },
    {
      id: 'atmosphere',
      label: p('presetAtmosphere'),
      question: {
        text: p('presetAtmosphereQuestion'),
        options: [
          p('presetFantastic'),
          p('presetGood'),
          p('presetOk'),
          p('presetBad'),
        ],
      },
    },
    {
      id: 'rating',
      label: p('presetRating'),
      question: {
        text: p('presetRatingQuestion'),
        // Best first: ⭐⭐⭐⭐⭐ down to ⭐
        options: [5, 4, 3, 2, 1].map((stars) => '⭐'.repeat(stars)),
      },
    },
  ];

  const updateAfterQuestion = (key, changes) =>
    setAfterQuestions((questions) =>
      questions.map((q) => (q.key === key ? { ...q, ...changes } : q))
    );

  // A quick pick fills the first question if it's still empty, else adds one
  const addAfterQuestion = (preset) =>
    setAfterQuestions((questions) => {
      const isEmpty = (q) =>
        !q.text.trim() && q.type === 'options' && !filledOptions(q).length;
      if (preset && questions.length === 1 && isEmpty(questions[0])) {
        return [emptyAfterQuestion(preset)];
      }
      return [...questions, emptyAfterQuestion(preset)];
    });

  // The club's own team in the match, for the player picker's squad
  // (custom matches have no team ids: fall back to the first team)
  const clubTeamId = (match) =>
    (config.teams || []).find(
      (team) => team.id === match.homeTeam?.id || team.id === match.awayTeam?.id
    )?.id || config.teams?.[0]?.id;

  const playerQuestion = (title, teamId) => ({
    title,
    description: '',
    type: 'PLAYER_PICKER',
    isRequired: true,
    headingVideos: [],
    imageHeaderType: 'NONE',
    value: { teamId },
  });

  // A push for one poll, with {match} replaced by the teams; none if off
  const pushFor = (push, match) =>
    push.on && push.title?.trim() && push.message?.trim()
      ? {
          title: push.title
            .trim()
            .replaceAll('{match}', matchQuestionTitle(match)),
          body: push.message
            .trim()
            .replaceAll('{match}', matchQuestionTitle(match)),
        }
      : null;
  const pushCount = isQuizzes
    ? chosenMatches.length * (pushQuiz.on ? 1 : 0)
    : chosenMatches.length *
      ((beforeOn && pushBefore.on ? 1 : 0) + (afterOn && pushAfter.on ? 1 : 0));

  const createPolls = () => {
    const items = [];
    chosenMatches.forEach((match) => {
      const kickOff = moment.utc(match.matchStart);
      const base = {
        title: matchQuestionTitle(match),
        description: [
          kickOff.clone().local().format('dddd D MMMM HH:mm'),
          leagueName(match),
        ]
          .filter(Boolean)
          .join(' · '),
      };
      if (beforeOn) {
        items.push({
          ...base,
          subtitle: p('pollBeforeSubtitle'),
          // Opens the set hours before kick-off, but never in the past
          startDate: moment
            .max(
              moment(),
              beforeHours === ''
                ? moment()
                : kickOff.clone().subtract(Number(beforeHours) || 0, 'hours')
            )
            .toISOString(),
          endDate: kickOff.toISOString(),
          push: pushFor(pushBefore, match),
          questions: [
            radioQuestion(beforeQuestion.trim() || p('pollWhoWins'), [
              teamName(match, 'homeTeam'),
              ...(includeDraw ? [p('pollDraw')] : []),
              teamName(match, 'awayTeam'),
            ]),
          ],
        });
      }
      if (afterOn) {
        const opens = kickOff
          .clone()
          .add(Math.max(0, Number(afterStartHours) || 0), 'hours');
        items.push({
          ...base,
          subtitle: p('pollAfterSubtitle'),
          startDate: opens.toISOString(),
          endDate: opens
            .clone()
            .add(Math.max(1, Number(afterOpenHours) || 1), 'hours')
            .toISOString(),
          push: pushFor(pushAfter, match),
          questions: afterQuestions.map((question) =>
            question.type === 'player'
              ? playerQuestion(question.text.trim(), clubTeamId(match))
              : radioQuestion(question.text.trim(), filledOptions(question))
          ),
        });
      }
    });
    onCreate(null, { items, categoryId: categoryId || null, image });
  };

  const renderTeam = (match, side) => {
    const logo = teamLogo(match, side);
    return (
      <span className="match-pick__team">
        <span className="match-pick__logo">
          {logo && <img src={logo} alt="" />}
        </span>
        {teamName(match, side)}
      </span>
    );
  };

  const pointsField = (
    <label className="match-pick__field">
      <span className="form-header">{qT('setPoints')}</span>
      <input
        type="number"
        min="0"
        step="1"
        className="form-control form-control-sm match-pick__points-input"
        value={points}
        onChange={(e) => setPoints(e.target.value)}
      />
    </label>
  );

  const renderMatchStep = () => (
    <>
      <div className="match-pick__bar">
        <input
          type="search"
          className="form-control form-control-sm match-pick__search"
          placeholder={p('searchTeam')}
          aria-label={p('searchTeam')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="form-control form-control-sm match-pick__league"
          value={league}
          onChange={(e) => setLeague(e.target.value)}
          aria-label={p('allLeagues')}
        >
          <option value="">{p('allLeagues')}</option>
          {leagues.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        {!isSteps && pointsField}
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm mb-0 match-pick__all"
          disabled={!selectable.length}
          onClick={toggleAll}
        >
          {allSelected ? p('deselectAll') : s('selectAll')}
        </button>
      </div>

      <div className="match-pick__list">
        {loading && (
          <div className="match-pick__empty">{p('loadingMatches')}</div>
        )}
        {!loading && !visible.length && (
          <div className="match-pick__empty">{p('noUpcomingMatches')}</div>
        )}
        {!loading &&
          visible.map((match) => {
            const added = isAdded(match);
            const checked = added || selected.includes(match.id);
            return (
              <label
                key={match.id}
                className={`match-pick__row${
                  added ? ' match-pick__row--added' : ''
                }${checked && !added ? ' match-pick__row--on' : ''}`}
              >
                <input
                  type="checkbox"
                  className="match-pick__check"
                  checked={checked}
                  disabled={added}
                  onChange={() => toggle(match.id)}
                />
                <span className="match-pick__when">
                  <strong>
                    {moment.utc(match.matchStart).local().format('DD MMM')}
                  </strong>
                  <small>
                    {moment.utc(match.matchStart).local().format('ddd HH:mm')}
                  </small>
                </span>
                <span className="match-pick__teams">
                  {renderTeam(match, 'homeTeam')}
                  {renderTeam(match, 'awayTeam')}
                </span>
                <span className="match-pick__league-name">
                  {added ? p('matchAlreadyAdded') : leagueName(match)}
                </span>
              </label>
            );
          })}
      </div>
    </>
  );

  const summaryBlock = (
    <div className="match-pick__summary">
      <div className="match-pick__summary-head">
        <span className="form-header m-0">
          {p('matchesSelected', { count: chosenMatches.length })}
        </span>
        <button
          type="button"
          className="btn btn-link btn-sm p-0 mb-0"
          onClick={() => setStep(1)}
        >
          {p('changeMatches')}
        </button>
      </div>
      <div className="match-pick__chips">
        {chosenMatches.map((match) => (
          <span className="match-pick__chip" key={match.id}>
            {matchQuestionTitle(match)}
            <small>
              {moment.utc(match.matchStart).local().format('DD MMM')}
            </small>
          </span>
        ))}
      </div>
    </div>
  );

  const categoryField = (
    <label className="match-pick__field">
      <span className="form-header">{s('category')}</span>
      <select
        className="form-control form-control-sm"
        value={categoryId}
        onChange={(e) => setCategoryId(e.target.value)}
      >
        <option value="">{p('noCategory')}</option>
        {categories.map((category) => (
          <option key={category.id} value={category.id}>
            {category.title || category.name}
          </option>
        ))}
      </select>
    </label>
  );

  const imageField = (
    <div className="match-pick__field form-grid__full">
      <span className="form-header">{p('quizImage')}</span>
      <div className="match-pick__image-row">
        <span className="match-pick__thumb">
          {image && <img src={image} alt="" />}
        </span>
        <div className="match-pick__image-actions">
          <label className="btn btn-outline-secondary btn-sm mb-0">
            {image ? p('changeImage') : p('chooseImage')}
            <input type="file" accept="image/*" hidden onChange={pickImage} />
          </label>
          {image && (
            <button
              type="button"
              className="btn btn-link btn-sm mb-0 p-0"
              onClick={() => setImage(null)}
            >
              {p('removeImage')}
            </button>
          )}
          <small className="text-muted">
            {s('recommendedImageSize')} 16:9 (1035 x 582 px)
          </small>
        </div>
      </div>
    </div>
  );

  const renderSettingsStep = () => (
    <div className="match-pick__settings-step">
      {summaryBlock}
      <div className="form-grid">
        <label className="match-pick__field form-grid__full">
          <span className="form-header">{s('subtitle')}</span>
          <input
            type="text"
            className="form-control form-control-sm"
            value={subtitle}
            placeholder={p('guessTheResult')}
            onChange={(e) => setSubtitle(e.target.value)}
          />
        </label>
        {categoryField}
        {pointsField}

        {imageField}
        <div className="match-pick__field form-grid__full">
          <span className="form-header">
            {p('extraQuestions')}
            {extraIds.length > 0 && (
              <span className="text-muted"> · {extraIds.length}</span>
            )}
          </span>
          <div className="match-pick__library">
            {!library.length && (
              <span className="match-pick__library-empty">
                {p('noLibraryQuestions')}
              </span>
            )}
            {library.map((question) => (
              <label key={question.id} className="match-pick__library-row">
                <input
                  type="checkbox"
                  className="match-pick__check"
                  checked={extraIds.includes(question.id)}
                  onChange={() => toggleExtra(question.id)}
                />
                <span className="match-pick__library-title">
                  {question.title}
                </span>
                <span className="text-muted small">
                  {p(`questionType-${question.type}`)}
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderPollsStep = () => (
    <div className="match-pick__settings-step">
      {summaryBlock}

      <div className="match-pick__poll">
        <label className="match-pick__poll-toggle">
          <input
            type="checkbox"
            className="match-pick__check"
            checked={beforeOn}
            onChange={() => setBeforeOn(!beforeOn)}
          />
          <span>
            <strong>{p('pollBeforeSubtitle')}</strong>
            <small>{p('pollBeforeHint')}</small>
          </span>
        </label>
        {beforeOn && (
          <div className="match-pick__poll-body">
            <label className="match-pick__field">
              <span className="form-header">{qT('question')}</span>
              <input
                type="text"
                className="form-control form-control-sm"
                value={beforeQuestion}
                placeholder={p('pollWhoWins')}
                onChange={(e) => setBeforeQuestion(e.target.value)}
              />
            </label>
            <label className="match-pick__inline-check">
              <input
                type="checkbox"
                className="match-pick__check"
                checked={includeDraw}
                onChange={() => setIncludeDraw(!includeDraw)}
              />
              {p('pollIncludeDraw')}
            </label>
            <label className="match-pick__field match-pick__field--short">
              <span className="form-header">{p('pollOpensBefore')}</span>
              <input
                type="number"
                min="0"
                className="form-control form-control-sm"
                value={beforeHours}
                placeholder={p('pollOpensNow')}
                onChange={(e) => setBeforeHours(e.target.value)}
              />
            </label>
          </div>
        )}
      </div>

      <div className="match-pick__poll">
        <label className="match-pick__poll-toggle">
          <input
            type="checkbox"
            className="match-pick__check"
            checked={afterOn}
            onChange={() => setAfterOn(!afterOn)}
          />
          <span>
            <strong>{p('pollAfterSubtitle')}</strong>
            <small>{p('pollAfterHint')}</small>
          </span>
        </label>
        {afterOn && (
          <div className="match-pick__poll-body">
            {/* Quick picks fill in a ready-made question */}
            <div className="match-pick__field">
              <span className="form-header">{p('pollQuickPicks')}</span>
              <div className="match-pick__pills">
                {afterPresets.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    className="btn btn-outline-secondary btn-sm mb-0 match-pick__preset"
                    onClick={() => addAfterQuestion(preset.question)}
                  >
                    <PlusIcon size={14} />
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Same card as the event's registration questions: number,
                question and options, then a footer with the answer type */}
            {afterQuestions.map((question, qIndex) => (
              <div className="qb-question match-pick__qcard" key={question.key}>
                <div className="qb-question__main">
                  <div className="qb-question__rail">
                    <span className="qb-question__num">{qIndex + 1}</span>
                  </div>
                  <div className="qb-question__body">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={question.text}
                      placeholder={qT('enterQuestion')}
                      aria-label={qT('question')}
                      onChange={(e) =>
                        updateAfterQuestion(question.key, {
                          text: e.target.value,
                        })
                      }
                    />
                    {question.type === 'options' ? (
                      <div className="match-pick__options">
                        {question.options.map((option, index) => (
                          <div className="match-pick__option" key={index}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={option}
                              placeholder={qT('optionNumber', {
                                number: index + 1,
                              })}
                              aria-label={qT('optionNumber', {
                                number: index + 1,
                              })}
                              onChange={(e) =>
                                updateAfterQuestion(question.key, {
                                  options: question.options.map((o, i) =>
                                    i === index ? e.target.value : o
                                  ),
                                })
                              }
                            />
                            <button
                              type="button"
                              className="square-icon-btn"
                              aria-label={qT('removeOption')}
                              disabled={question.options.length <= 2}
                              onClick={() =>
                                updateAfterQuestion(question.key, {
                                  options: question.options.filter(
                                    (_, i) => i !== index
                                  ),
                                })
                              }
                            >
                              <CloseIcon />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="qm-add-row"
                          onClick={() =>
                            updateAfterQuestion(question.key, {
                              options: [...question.options, ''],
                            })
                          }
                        >
                          <PlusIcon size={16} />
                          {qT('reservationOptionAdd')}
                        </button>
                      </div>
                    ) : (
                      <small className="qb-question__note">
                        {p('pollPlayerHint')}
                      </small>
                    )}
                  </div>
                </div>
                <div className="qb-question__foot">
                  <div className="qb-ask">
                    <span>{p('pollAnswerType')}</span>
                    <div
                      className="qb-segment"
                      role="radiogroup"
                      aria-label={p('pollAnswerType')}
                    >
                      {[
                        ['options', p('pollAnswerOptions')],
                        ['player', p('questionType-PLAYER_PICKER')],
                      ].map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          role="radio"
                          aria-checked={question.type === value}
                          className={`qb-segment__btn${
                            question.type === value
                              ? ' qb-segment__btn--on'
                              : ''
                          }`}
                          onClick={() =>
                            updateAfterQuestion(question.key, { type: value })
                          }
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {afterQuestions.length > 1 && (
                    <div className="qb-question__actions">
                      <SquareIconButton
                        icon={<DeleteOutlineIcon />}
                        title={p('removeQuestion')}
                        tone="danger"
                        id={`remove-poll-question-${question.key}`}
                        onClick={() =>
                          setAfterQuestions(
                            afterQuestions.filter((q) => q.key !== question.key)
                          )
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
            <button
              type="button"
              className="qm-add-row mb-3"
              onClick={() => addAfterQuestion()}
            >
              <PlusIcon size={16} />
              {p('addPollQuestion')}
            </button>
            <div className="form-grid">
              <label className="match-pick__field">
                <span className="form-header">{p('pollOpensAfter')}</span>
                <input
                  type="number"
                  min="0"
                  className="form-control form-control-sm"
                  value={afterStartHours}
                  onChange={(e) => setAfterStartHours(e.target.value)}
                />
              </label>
              <label className="match-pick__field">
                <span className="form-header">{p('pollOpenFor')}</span>
                <input
                  type="number"
                  min="1"
                  className="form-control form-control-sm"
                  value={afterOpenHours}
                  onChange={(e) => setAfterOpenHours(e.target.value)}
                />
              </label>
            </div>
          </div>
        )}
      </div>

      <div className="form-grid">
        {categoryField}
        {imageField}
      </div>
    </div>
  );

  const renderPushCard = (push, setPush, label, when) => (
    <div className="match-pick__poll">
      <label className="match-pick__poll-toggle">
        <input
          type="checkbox"
          className="match-pick__check"
          checked={!!push.on}
          onChange={() => setPush({ ...push, on: !push.on })}
        />
        <span>
          <strong>{label}</strong>
          <small>{when}</small>
        </span>
      </label>
      {push.on && (
        <div className="match-pick__poll-body">
          <label className="match-pick__field">
            <span className="form-header">{s('title')}</span>
            <input
              type="text"
              className="form-control form-control-sm"
              value={push.title}
              onChange={(e) => setPush({ ...push, title: e.target.value })}
            />
          </label>
          <label className="match-pick__field">
            <span className="form-header">{s('message')}</span>
            <textarea
              rows={2}
              className="form-control form-control-sm"
              value={push.message}
              onChange={(e) => setPush({ ...push, message: e.target.value })}
            />
          </label>
        </div>
      )}
    </div>
  );

  const renderQuizPushStep = () => (
    <div className="match-pick__settings-step">
      <p className="match-pick__push-info">{p('pushQuizStepInfo')}</p>
      {renderPushCard(
        pushQuiz,
        setPushQuiz,
        p('pushWhenOpens'),
        pushQuiz.hours === ''
          ? p('pushSentNow')
          : p('pushQuizSentHoursBefore', { hours: pushQuiz.hours })
      )}
      {pushQuiz.on && (
        <label className="match-pick__field match-pick__field--short">
          <span className="form-header">{p('pushSendHoursBefore')}</span>
          <input
            type="number"
            min="0"
            className="form-control form-control-sm"
            value={pushQuiz.hours}
            placeholder={p('pollOpensNow')}
            onChange={(e) =>
              setPushQuiz({ ...pushQuiz, hours: e.target.value })
            }
          />
        </label>
      )}
      <small className="text-muted d-block mb-3">{p('pushMatchToken')}</small>
    </div>
  );

  const renderPushStep = () => (
    <div className="match-pick__settings-step">
      <p className="match-pick__push-info">{p('pushStepInfo')}</p>
      {beforeOn &&
        renderPushCard(
          pushBefore,
          setPushBefore,
          `${p('pushWhenOpens')} · ${p('pollBeforeSubtitle')}`,
          beforeHours === ''
            ? p('pushSentNow')
            : p('pushSentHoursBefore', { hours: beforeHours })
        )}
      {afterOn &&
        renderPushCard(
          pushAfter,
          setPushAfter,
          `${p('pushWhenOpens')} · ${p('pollAfterSubtitle')}`,
          p('pushSentHoursAfter', { hours: afterStartHours || 0 })
        )}
      <small className="text-muted d-block mb-3">{p('pushMatchToken')}</small>
    </div>
  );

  const onSettings = isSteps && step === 2;
  const onPush = isSteps && step === 3;
  const nextDisabled =
    step === 1 ? !selected.length : isPolls && step === 2 && !pollsValid;

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      size="lg"
      contentClassName="qm"
    >
      <div className="qm-header">
        <div>
          <h2 className="qm-header__title">
            {isPolls
              ? p('matchPollsTitle')
              : isQuizzes
                ? p('matchQuizzesTitle')
                : p('matchQuestionsTitle')}
          </h2>
          <p className="qm-header__subtitle">
            {isPolls
              ? p('matchPollsSubtitle')
              : isQuizzes
                ? p('matchQuizzesSubtitle')
                : p('matchQuestionsSubtitle')}
          </p>
          {isSteps && (
            <ol className="match-pick__steps">
              {[
                p('stepMatches'),
                isPolls ? p('stepPolls') : p('stepSettings'),
                p('stepNotifications'),
              ].map((label, i) => (
                <li
                  key={label}
                  className={`match-pick__step${
                    step === i + 1 ? ' match-pick__step--on' : ''
                  }${step > i + 1 ? ' match-pick__step--done' : ''}`}
                >
                  <span className="match-pick__step-num">{i + 1}</span>
                  {label}
                </li>
              ))}
            </ol>
          )}
        </div>
        <button
          type="button"
          className="qm-header__close"
          onClick={onClose}
          aria-label={qT('close')}
        >
          <CloseIcon size={20} />
        </button>
      </div>

      {step === 1 && renderMatchStep()}
      {onSettings && (isPolls ? renderPollsStep() : renderSettingsStep())}
      {onPush && (isPolls ? renderPushStep() : renderQuizPushStep())}

      <div className="qm-footer match-pick__footer">
        <span className="match-pick__count">
          {selected.length > 0 &&
            step === 1 &&
            p('selectedCount', { count: selected.length })}
          {onPush && pushCount > 0 && p('pushCountLabel', { count: pushCount })}
        </span>
        {step > 1 ? (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm mb-0"
            onClick={() => setStep(step - 1)}
          >
            {p('back')}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm mb-0"
            onClick={onClose}
          >
            {qT('cancel')}
          </button>
        )}
        {isSteps && step < lastStep ? (
          <button
            type="button"
            className="btn btn-primary btn-sm mb-0"
            disabled={nextDisabled}
            onClick={() => setStep(step + 1)}
          >
            {p('next')}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary btn-sm mb-0"
            disabled={!selected.length || (isPolls && !pollsValid)}
            onClick={isPolls ? createPolls : create}
          >
            {isPolls && p('createMatchPolls', { count: pollCount })}
            {isQuizzes && p('createMatchQuizzes', { count: selected.length })}
            {!isSteps && p('createMatchQuestions', { count: selected.length })}
          </button>
        )}
      </div>
    </Modal>
  );
};

MatchQuestionsModal.defaultProps = {
  mode: 'questions',
  existingTitles: [],
};

export default MatchQuestionsModal;
